import { Features, FeaturesSchema, Limits, Toggles, PlanCheck, CheckContext, PlanValidationResult } from "./types";

/**
 * Get a specific limit value from features
 */
export function getLimit<T extends keyof Limits>(
  features: Features,
  key: T,
  fallback: number = 0
): number {
  return features?.limits?.[key] ?? fallback;
}

/**
 * Check if a toggle is enabled
 */
export function hasToggle<T extends keyof Toggles>(
  features: Features,
  key: T
): boolean {
  return Boolean(features?.toggles?.[key]);
}

/**
 * Safely parse features JSON data
 */
export function parseFeatures(raw: unknown): Features {
  const parsed = FeaturesSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn("Failed to parse features:", parsed.error);
    return { limits: {}, toggles: {} };
  }
  return parsed.data;
}

/**
 * Validate a plan check against features
 */
export function validatePlanCheck(
  features: Features,
  check: PlanCheck,
  currentUsage?: number
): PlanValidationResult {
  if (check.type === "toggle") {
    const allowed = hasToggle(features, check.key);
    return {
      allowed,
      reason: allowed ? undefined : "feature_not_in_plan"
    };
  }

  if (check.type === "limit") {
    const cap = getLimit(features, check.key, 0);
    const used = currentUsage ?? 0;
    const delta = check.delta ?? 1;
    const allowed = used + delta <= cap;

    return {
      allowed,
      reason: allowed ? undefined : "limit_reached",
      cap,
      used
    };
  }

  return {
    allowed: false,
    reason: "invalid_check"
  };
}

/**
 * Get formatted error message for plan validation failure
 */
export function getPlanErrorMessage(result: PlanValidationResult, featureName?: string): string {
  if (result.allowed) return "";

  switch (result.reason) {
    case "feature_not_in_plan":
      return `${featureName || "Esta funcionalidade"} não está disponível no seu plano atual. Faça upgrade para liberar.`;
    case "limit_reached":
      return `Limite atingido: ${result.used}/${result.cap}. Faça upgrade para continuar.`;
    case "invalid_check":
      return "Verificação de plano inválida.";
    default:
      return "Recurso não disponível no seu plano atual.";
  }
}

/**
 * Common usage metrics for different systems
 */
export const USAGE_METRICS = {
  FLEET: {
    USERS: "users",
    VEHICLES: "vehicles",
    DRIVERS: "drivers",
    TRIPS: "monthly_trips",
  },
} as const;

/**
 * Feature display names for UI
 */
export const FEATURE_NAMES = {
  // FleetONE
  fleet_users_max: "Usuários",
  fleet_vehicles_max: "Veículos",
  fleet_storage_gb: "Armazenamento",
  
  // TrackingONE
  tracking_users_max: "Usuários",
  tracking_vehicles_max: "Veículos Rastreados",
  tracking_devices_max: "Dispositivos GPS",
  tracking_storage_gb: "Armazenamento",
  tracking_device_purchase_allowed: "Compra de Dispositivos GPS",
  tracking_chip_purchase_allowed: "Compra de Chips M2M",
  
  // Genéricos (backwards compatibility)
  users_max: "Usuários",
  vehicles_max: "Veículos",
  storage_gb: "Armazenamento",
  
  // Toggles
  basic_reports: "Relatórios Básicos",
  advanced_analytics: "Analytics Avançado",
  integrations: "Integrações",
  custom_integrations: "Integrações Personalizadas",
  api_access: "Acesso à API",
  mobile_app: "App Mobile",
  priority_support: "Suporte Prioritário",
  custom_branding: "Marca Personalizada",
  audit_logs: "Logs de Auditoria",
  multi_company: "Multi-Empresa",
} as const;

/**
 * Plan status display configurations
 */
export const PLAN_STATUS = {
  active: { label: "Ativo", color: "success" },
  pending: { label: "Pendente", color: "warning" },
  suspended: { label: "Suspenso", color: "destructive" },
  canceled: { label: "Cancelado", color: "secondary" },
} as const;

/**
 * System configurations - matching database codes
 */
export const SYSTEM_CONFIG = {
  fleet: { 
    name: "FleetONE", 
    description: "Gestão de Frota Inteligente",
    color: "blue", 
    icon: "truck",
    gradient: "from-blue-500 to-blue-600"
  },
} as const;

/**
 * System-specific field configurations for plan forms
 */
export const SYSTEM_FIELD_CONFIG = {
  fleet: {
    limits: ['fleet_vehicles_max'],
    labels: {
      fleet_vehicles_max: "Veículos",
    }
  },
} as const;